import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { 
  Camera, X, Check, RotateCcw, Sun, 
  Contrast, Palette, Sparkles, Video
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Video filter presets
const FILTER_PRESETS = [
  { id: 'none', name: 'Normal', filter: '' },
  { id: 'warm', name: 'Warm', filter: 'sepia(20%) saturate(120%)' },
  { id: 'cool', name: 'Cool', filter: 'hue-rotate(180deg) saturate(80%)' },
  { id: 'vintage', name: 'Vintage', filter: 'sepia(40%) contrast(90%) brightness(90%)' },
  { id: 'bw', name: 'B&W', filter: 'grayscale(100%)' },
  { id: 'bright', name: 'Bright', filter: 'brightness(120%) contrast(110%)' },
  { id: 'dramatic', name: 'Dramatic', filter: 'contrast(130%) saturate(120%)' },
  { id: 'fade', name: 'Fade', filter: 'contrast(90%) brightness(110%) saturate(80%)' },
];

export const VideoFilterEditor = ({ 
  videoSrc, 
  onSave, 
  onCancel,
  className 
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [customFilters, setCustomFilters] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100
  });
  const [showCustom, setShowCustom] = useState(false);

  const getFilterStyle = useCallback(() => {
    const preset = FILTER_PRESETS.find(f => f.id === selectedFilter);
    const presetFilter = preset?.filter || '';
    
    if (showCustom) {
      return `${presetFilter} brightness(${customFilters.brightness}%) contrast(${customFilters.contrast}%) saturate(${customFilters.saturation}%)`;
    }
    return presetFilter;
  }, [selectedFilter, customFilters, showCustom]);

  const handleSave = async () => {
    if (!videoRef.current) return;
    
    // For now, we'll save the filter settings
    // In production, you'd want to process the video with FFmpeg
    onSave({
      originalSrc: videoSrc,
      filter: getFilterStyle(),
      filterPreset: selectedFilter,
      customFilters: showCustom ? customFilters : null
    });
  };

  const resetFilters = () => {
    setSelectedFilter('none');
    setCustomFilters({ brightness: 100, contrast: 100, saturation: 100 });
    setShowCustom(false);
  };

  return (
    <div className={cn("fixed inset-0 bg-black z-50", className)} data-testid="video-filter-editor">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10 bg-gradient-to-b from-black/80 to-transparent">
        <Button 
          variant="ghost" 
          size="icon" 
          className="text-white rounded-full"
          onClick={onCancel}
        >
          <X className="w-6 h-6" />
        </Button>
        
        <Button
          variant="ghost"
          size="icon"
          className="text-white rounded-full"
          onClick={resetFilters}
        >
          <RotateCcw className="w-5 h-5" />
        </Button>
        
        <Button
          className="rounded-full bg-primary"
          onClick={handleSave}
          data-testid="save-filter-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          Save
        </Button>
      </div>

      {/* Video Preview */}
      <div className="flex items-center justify-center h-full">
        <video
          ref={videoRef}
          src={videoSrc}
          className="max-w-full max-h-[60vh] rounded-2xl"
          style={{ filter: getFilterStyle() }}
          autoPlay
          loop
          muted
          playsInline
        />
      </div>

      {/* Filter Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
        {/* Custom Adjustments Toggle */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <Button
            variant={!showCustom ? "default" : "ghost"}
            size="sm"
            className="rounded-full text-white"
            onClick={() => setShowCustom(false)}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Filters
          </Button>
          <Button
            variant={showCustom ? "default" : "ghost"}
            size="sm"
            className="rounded-full text-white"
            onClick={() => setShowCustom(true)}
          >
            <Palette className="w-4 h-4 mr-2" />
            Adjust
          </Button>
        </div>

        {showCustom ? (
          /* Custom Sliders */
          <div className="space-y-4 max-w-md mx-auto">
            <div className="flex items-center gap-4">
              <Sun className="w-5 h-5 text-white shrink-0" />
              <Slider
                value={[customFilters.brightness]}
                onValueChange={([val]) => setCustomFilters(prev => ({ ...prev, brightness: val }))}
                min={50}
                max={150}
                step={1}
                className="flex-1"
              />
              <span className="text-white text-sm w-10">{customFilters.brightness}%</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Contrast className="w-5 h-5 text-white shrink-0" />
              <Slider
                value={[customFilters.contrast]}
                onValueChange={([val]) => setCustomFilters(prev => ({ ...prev, contrast: val }))}
                min={50}
                max={150}
                step={1}
                className="flex-1"
              />
              <span className="text-white text-sm w-10">{customFilters.contrast}%</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Palette className="w-5 h-5 text-white shrink-0" />
              <Slider
                value={[customFilters.saturation]}
                onValueChange={([val]) => setCustomFilters(prev => ({ ...prev, saturation: val }))}
                min={0}
                max={200}
                step={1}
                className="flex-1"
              />
              <span className="text-white text-sm w-10">{customFilters.saturation}%</span>
            </div>
          </div>
        ) : (
          /* Filter Presets */
          <div className="flex gap-3 overflow-x-auto pb-2 px-4 -mx-4 scrollbar-hide">
            {FILTER_PRESETS.map((preset) => (
              <button
                key={preset.id}
                onClick={() => setSelectedFilter(preset.id)}
                className={cn(
                  "flex flex-col items-center shrink-0 transition-all",
                  selectedFilter === preset.id && "scale-110"
                )}
                data-testid={`filter-${preset.id}`}
              >
                <div className={cn(
                  "w-16 h-16 rounded-2xl overflow-hidden border-2 mb-1",
                  selectedFilter === preset.id ? "border-primary" : "border-transparent"
                )}>
                  <video
                    src={videoSrc}
                    className="w-full h-full object-cover"
                    style={{ filter: preset.filter }}
                    muted
                    playsInline
                  />
                </div>
                <span className={cn(
                  "text-xs",
                  selectedFilter === preset.id ? "text-primary font-semibold" : "text-white/70"
                )}>
                  {preset.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Image filter for photos
export const ImageFilterEditor = ({ 
  imageSrc, 
  onSave, 
  onCancel 
}) => {
  const [selectedFilter, setSelectedFilter] = useState('none');

  const handleSave = () => {
    const preset = FILTER_PRESETS.find(f => f.id === selectedFilter);
    onSave({
      originalSrc: imageSrc,
      filter: preset?.filter || '',
      filterPreset: selectedFilter
    });
  };

  return (
    <div className="fixed inset-0 bg-black z-50" data-testid="image-filter-editor">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10 bg-gradient-to-b from-black/80 to-transparent">
        <Button 
          variant="ghost" 
          size="icon" 
          className="text-white rounded-full"
          onClick={onCancel}
        >
          <X className="w-6 h-6" />
        </Button>
        
        <Button
          className="rounded-full bg-primary"
          onClick={handleSave}
        >
          <Check className="w-5 h-5 mr-2" />
          Save
        </Button>
      </div>

      {/* Image Preview */}
      <div className="flex items-center justify-center h-full">
        <img
          src={imageSrc}
          alt="Preview"
          className="max-w-full max-h-[60vh] rounded-2xl object-contain"
          style={{ filter: FILTER_PRESETS.find(f => f.id === selectedFilter)?.filter || '' }}
        />
      </div>

      {/* Filter Presets */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
        <div className="flex gap-3 overflow-x-auto pb-2 px-4 -mx-4">
          {FILTER_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => setSelectedFilter(preset.id)}
              className={cn(
                "flex flex-col items-center shrink-0 transition-all",
                selectedFilter === preset.id && "scale-110"
              )}
            >
              <div className={cn(
                "w-16 h-16 rounded-2xl overflow-hidden border-2 mb-1",
                selectedFilter === preset.id ? "border-primary" : "border-transparent"
              )}>
                <img
                  src={imageSrc}
                  alt={preset.name}
                  className="w-full h-full object-cover"
                  style={{ filter: preset.filter }}
                />
              </div>
              <span className={cn(
                "text-xs",
                selectedFilter === preset.id ? "text-primary font-semibold" : "text-white/70"
              )}>
                {preset.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VideoFilterEditor;
